import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { colors, layout, mediaQuery } from '@modernhealth/vital-foundation';
import { Redirect, useParams } from 'react-router';
import {
    BodyTextMedium,
    ComponentTextMedium,
    Grid,
    GridItem,
    HeadingText40,
    HeadingText50,
} from '@modernhealth/vital-components';
import { captureEvent } from '@sentry/browser';
import { useTranslation } from 'react-i18next';
import { useFetchTopicsQuery } from '@modernhealth/circles-graphql/v2';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { BackButton } from '../components/BackButton';
import { CirclesSeriesPageSkeleton } from '../skeletons/CirclesSeriesPageSkeleton';
import { sortTopicsByFirstSession } from '../utils/sortTopicsByFirstSession';
import { CircleTopicCard } from '../components/CircleTopicCard';
import { TranslatedSeriesAccess } from '../components/TranslatedSeriesAccess';
import { CircleOnDemandCard } from '../components/CircleOnDemandCard';
import { CircleOnDemandRow } from '../components/CircleOnDemandRow';

const Wrapper = styled.div({
    width: '100%',
    maxWidth: layout.containerWidth,
    margin: '0 auto',
    paddingLeft: layout.containerPaddingX,
    paddingRight: layout.containerPaddingX,
    paddingBottom: layout.footerHeight,
    marginTop: 30,
});

const HeroWrapper = styled.div({
    display: 'flex',
    flexDirection: 'column-reverse',
    flexWrap: 'wrap',
    marginBottom: 36,
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    [mediaQuery.minWidth('md')]: {
        flexDirection: 'row',
    },
});

const HeaderTextWrapper = styled.div({
    width: '100%',
    marginTop: 30,
    [mediaQuery.minWidth('md')]: {
        width: '50%',
        paddingRight: 50,
        marginTop: 0,
    },
});

const HeaderImageWrapper = styled.div({
    width: '100%',
    borderRadius: 12,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end',
    [mediaQuery.minWidth('md')]: {
        width: '50%',
    },
});

const HeaderImage = styled.img({
    width: '100%',
    borderRadius: 12,
});

interface CircleSeriesProps {
    seriesSlug: string;
}

const CircleSeries: React.FC<CircleSeriesProps> = ({ seriesSlug }) => {
    const { t } = useTranslation('circles');
    const { circlesShowOnDemand } = useFlags();
    const { data, loading } = useFetchTopicsQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            seriesSlug,
            limit: 100,
        },
    });

    const count = data?.circleSeriesBySlug?.topics?.totalCount;

    useEffect(() => {
        if (loading) return;

        if (count && count >= 100) {
            captureEvent({ message: `Circle topics were truncated at 100!`, extra: { count } });
        }
    }, [count, loading]);

    if (loading && !data) {
        return (
            <Wrapper>
                <CirclesSeriesPageSkeleton />
            </Wrapper>
        );
    }

    const series = data?.circleSeriesBySlug;
    if (!series) {
        // TODO: Error state

        return <Redirect to="/circles" />;
    }

    const sessionsCount = series.topics?.edges
        .map(topic => {
            return topic?.node.sessions?.totalCount || 0;
        })
        .reduce((previousValue, currentValue) => previousValue + currentValue, 0);

    const topics = sortTopicsByFirstSession(series.topics.edges);
    const { name, description, heroImage, accessType, onDemand } = series;

    return (
        <Wrapper>
            <HeroWrapper>
                <HeaderTextWrapper>
                    <BackButton fallbackPath="/circles">
                        {previousRoute => {
                            if (previousRoute === '/circles/upcoming') {
                                return t('routes.upcoming.title');
                            } else if (previousRoute === `/circles`) {
                                return t('routes.fallback.title');
                            }

                            return null;
                        }}
                    </BackButton>

                    {accessType ? (
                        <ComponentTextMedium
                            fontColor="gray65"
                            css={{
                                marginTop: 16,
                                marginBottom: 6,
                                fontWeight: 500,
                                color: colors.violet55,
                                textTransform: 'uppercase',
                            }}
                        >
                            <TranslatedSeriesAccess accessType={accessType} />
                        </ComponentTextMedium>
                    ) : null}

                    <HeadingText50 as="h2" marginBottom={12}>
                        {name}
                    </HeadingText50>

                    <BodyTextMedium css={{ lineHeight: '1.75rem' }}>{description}</BodyTextMedium>
                </HeaderTextWrapper>
                <HeaderImageWrapper>
                    <HeaderImage src={heroImage || ''} />
                </HeaderImageWrapper>
            </HeroWrapper>
            {circlesShowOnDemand && onDemand.edges.length > 0 && (
                <CircleOnDemandRow header={t('pages.series.sections.ondemand_header')}>
                    {onDemand.edges.map((edge, i) => {
                        const ondemand = edge?.node;
                        if (!ondemand) return null;

                        return (
                            <CircleOnDemandCard
                                seconds={ondemand.video.durationInSeconds}
                                host={ondemand.facilitators[0]?.name}
                                languageCode={ondemand.languageCode}
                                thumb={ondemand.video.thumbnail}
                                key={`${ondemand.id}-${i}`}
                                name={ondemand.name}
                                slug={ondemand.slug}
                            />
                        );
                    })}
                </CircleOnDemandRow>
            )}
            {sessionsCount > 0 && (
                <>
                    <HeadingText40 marginTop={68} marginBottom={24}>
                        {t('pages.series.sections.topics.header')}
                    </HeadingText40>
                    <Grid lgMode="3columns">
                        {topics.map(topic => {
                            return topic?.sessions?.totalCount > 0 ? (
                                <GridItem
                                    lg={1}
                                    md={2}
                                    column
                                    key={`session-card-${topic.id}-sorted-by-time`}
                                >
                                    <CircleTopicCard
                                        topic={topic}
                                        id={`session-card-${topic.id}-sorted-by-time`}
                                    />
                                </GridItem>
                            ) : null;
                        })}
                    </Grid>
                </>
            )}
        </Wrapper>
    );
};

export const CirclesSeriesPage: React.FC<unknown> = () => {
    const { seriesSlug } = useParams<CircleSeriesProps>();

    if (!seriesSlug) {
        return <Redirect to="/circles" />;
    }

    return <CircleSeries seriesSlug={seriesSlug} />;
};
